/**
 * Admin — Break Intelligence Dashboard
 * ─────────────────────────────────────
 * v2.13.22 (2026-02) — Observational subsystem for the Break Learning
 * Engine. Displays per-tenant station learning profiles (minute
 * heatmap, hour distribution, averages, learning_status) WITHOUT
 * affecting any live classification decision.
 *
 * Hits:
 *   GET  /api/admin/break-intelligence/overview              (list)
 *   GET  /api/admin/break-intelligence/station/{id}          (detail)
 *   POST /api/admin/break-intelligence/recompute/{id}        (admin)
 *   POST /api/admin/break-intelligence/backfill/{id}?days=14 (admin)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Loader2, BarChart3, Brain, Database,
  Clock, ListMusic, Activity, AlertCircle, Download,
} from 'lucide-react';
import { getBackendUrl } from '../../utils/api';
import { useLanguage } from '../../contexts/LanguageContext';

const API = getBackendUrl();

const STATUS_HEX = {
  collecting:   '#94a3b8',  // slate
  warming_up:   '#f59e0b',  // amber
  ready:        '#16a34a',  // green
};

const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('el-GR'));
const fmtSec = (s) => {
  if (s == null || s === 0) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return r ? `${m}m ${r}s` : `${m}m`;
};

/* ── Minute heatmap (60 cells 6×10 grid) ──────────────────────────── */
const MinuteHeatmap = ({ probability }) => {
  const cells = Array.from({ length: 60 }, (_, i) => {
    const key = String(i).padStart(2, '0');
    const p = Math.max(0, Math.min(1, Number(probability?.[key]) || 0));
    return { key, p };
  });
  return (
    <div
      className="grid grid-cols-10 gap-1"
      data-testid="minute-heatmap"
    >
      {cells.map(({ key, p }) => {
        // Amber → green ramp as probability rises.
        const opacity = 0.15 + p * 0.85;
        return (
          <div
            key={key}
            className="aspect-square rounded text-[9px] flex items-center justify-center tabular-nums"
            style={{
              backgroundColor: `rgba(245, 158, 11, ${opacity})`,
              color: p > 0.4 ? '#0f172a' : 'rgba(255,255,255,0.6)',
              fontWeight: p > 0.5 ? 700 : 400,
            }}
            data-testid={`minute-cell-${key}`}
            title={`:${key} → probability ${(p * 100).toFixed(0)}%`}
          >
            {key}
          </div>
        );
      })}
    </div>
  );
};

/* ── Hour bar chart (24 cells) ────────────────────────────────────── */
const HourBars = ({ distribution }) => {
  const entries = Array.from({ length: 24 }, (_, h) => {
    const key = String(h).padStart(2, '0');
    return { h: key, v: Number(distribution?.[key]) || 0 };
  });
  const max = Math.max(1, ...entries.map((e) => e.v));
  return (
    <div className="flex gap-1 items-end h-32" data-testid="hour-bars">
      {entries.map(({ h, v }) => {
        const heightPct = (v / max) * 100;
        return (
          <div
            key={h}
            className="flex-1 flex flex-col items-center gap-1"
            title={`${h}:00 → ${v} breaks`}
            data-testid={`hour-bar-${h}`}
          >
            <div
              className="w-full rounded-t-sm bg-amber-500/70 hover:bg-amber-400"
              style={{ height: `${heightPct}%`, minHeight: v > 0 ? '4px' : '0' }}
            />
            <div className="text-[9px] text-white/40 tabular-nums">{h}</div>
          </div>
        );
      })}
    </div>
  );
};

/* ── Stat card ─────────────────────────────────────────────────────── */
const Stat = ({ label, value, accent, testId }) => (
  <div
    className="bg-white/5 border border-white/10 rounded-lg p-3"
    data-testid={testId}
  >
    <div className="text-[10px] uppercase tracking-wide text-white/45 mb-1">
      {label}
    </div>
    <div
      className="text-2xl font-bold tabular-nums"
      style={{ color: accent || '#fafafa' }}
    >
      {value}
    </div>
  </div>
);

/* ── Station detail panel ─────────────────────────────────────────── */
const StationDetail = ({ stationId, onClose }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(
        `${API}/api/admin/break-intelligence/station/${stationId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }, [stationId]);

  useEffect(() => { load(); }, [load]);

  const recompute = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(
        `${API}/api/admin/break-intelligence/recompute/${stationId}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      await load();
    } finally {
      setBusy(false);
    }
  };

  const backfill = async () => {
    if (!window.confirm(t('breakIntel_confirmBackfill') || 'Run 14-day backfill?')) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(
        `${API}/api/admin/break-intelligence/backfill/${stationId}?days=14`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      const j = await r.json();
      alert(`Backfill done. ${j.total_breaks_ingested} breaks ingested across ${j.days_processed} days.`);
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (busy && !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="break-intel-loading">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-300">
        <AlertCircle className="inline w-4 h-4 mr-2" />
        {error}
      </div>
    );
  }
  if (!data) return null;

  const p = data.profile || {};
  const statusHex = STATUS_HEX[p.learning_status] || STATUS_HEX.collecting;

  return (
    <div className="space-y-5" data-testid="break-intel-detail">
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={onClose}
            className="text-[12px] text-white/55 hover:text-white inline-flex items-center gap-1.5"
            data-testid="break-intel-close-detail"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <h2 className="text-2xl font-bold mt-1">
            {data.station_name || data.station_id}
          </h2>
          <div
            className="inline-flex items-center gap-2 mt-2 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide"
            style={{ backgroundColor: `${statusHex}22`, color: statusHex }}
            data-testid="break-intel-status-badge"
          >
            <Brain className="w-3 h-3" /> {p.learning_status}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={recompute}
            disabled={busy}
            className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/15 rounded text-[12px] inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="break-intel-recompute-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />
            {t('breakIntel_recompute') || 'Recompute'}
          </button>
          <button
            onClick={backfill}
            disabled={busy}
            className="px-3 py-1.5 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-200 rounded text-[12px] inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="break-intel-backfill-btn"
          >
            <Download className="w-3.5 h-3.5" />
            {t('breakIntel_backfill') || '14d Backfill'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Total Breaks" value={fmtNum(p.total_breaks)} accent="#fbbf24" testId="break-intel-stat-total" />
        <Stat label="Avg Duration" value={fmtSec(p.avg_break_duration_sec)} testId="break-intel-stat-duration" />
        <Stat label="Avg Songs Before" value={(p.avg_songs_before_break || 0).toFixed(1)} testId="break-intel-stat-songs" />
        <Stat label="Breaks / Hour" value={(p.avg_breaks_per_hour || 0).toFixed(2)} testId="break-intel-stat-rate" />
        <Stat label="Days With Data" value={fmtNum(p.days_with_data)} testId="break-intel-stat-days" />
      </div>

      <div className="bg-slate-900/40 border border-white/10 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-semibold uppercase tracking-wider text-white/80">
            Minute Probability Heatmap (00 → 59)
          </h3>
        </div>
        <MinuteHeatmap probability={p.minute_probability} />
        <div className="text-[10px] text-white/40 italic">
          {"Each cell = the minute's break frequency normalised against this station's peak minute."}
        </div>
      </div>

      <div className="bg-slate-900/40 border border-white/10 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-400" />
          <h3 className="text-sm font-semibold uppercase tracking-wider text-white/80">
            Hour Distribution
          </h3>
        </div>
        <HourBars distribution={p.hour_distribution} />
      </div>

      {/* Confidence weights — operator-spec 40/30/20/10 */}
      <div className="bg-slate-900/40 border border-white/10 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-purple-400" />
          <h3 className="text-sm font-semibold uppercase tracking-wider text-white/80">
            Confidence Weights
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[12px]">
          <div className="bg-white/5 rounded p-2.5">
            <div className="text-[10px] uppercase text-white/40">Classifier</div>
            <div className="text-xl font-bold tabular-nums">40%</div>
          </div>
          <div className="bg-white/5 rounded p-2.5">
            <div className="text-[10px] uppercase text-white/40">Minute</div>
            <div className="text-xl font-bold tabular-nums">30%</div>
          </div>
          <div className="bg-white/5 rounded p-2.5">
            <div className="text-[10px] uppercase text-white/40">Songs Before</div>
            <div className="text-xl font-bold tabular-nums">20%</div>
          </div>
          <div className="bg-white/5 rounded p-2.5">
            <div className="text-[10px] uppercase text-white/40">Duration</div>
            <div className="text-xl font-bold tabular-nums">10%</div>
          </div>
        </div>
        <div className="text-[10px] text-white/40 italic">
          Observational only — does NOT alter classification decisions.
        </div>
      </div>

      {/* Recent breaks */}
      {data.recent_breaks?.length > 0 && (
        <div className="bg-slate-900/40 border border-white/10 rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2">
            <ListMusic className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white/80">
              Recent Breaks (last 25)
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="recent-breaks-table">
              <thead className="text-white/45 uppercase text-[10px]">
                <tr>
                  <th className="text-left py-1 pr-3">When</th>
                  <th className="text-left py-1 pr-3">Hour:Min</th>
                  <th className="text-right py-1 pr-3">Duration</th>
                  <th className="text-right py-1 pr-3">Songs Before</th>
                  <th className="text-right py-1 pr-3">Listeners Δ</th>
                  <th className="text-right py-1">Classifier Conf.</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_breaks.map((r, idx) => (
                  <tr key={idx} className="border-t border-white/5">
                    <td className="py-1.5 pr-3 text-white/70 tabular-nums">
                      {new Date(r.break_start).toLocaleString('el-GR', { hour12: false })}
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums">
                      {String(r.hour).padStart(2, '0')}:{String(r.minute).padStart(2, '0')}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {fmtSec(r.duration_seconds)}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {r.songs_before_break ?? '—'}
                    </td>
                    <td
                      className="py-1.5 pr-3 text-right tabular-nums"
                      style={{ color: (r.listener_change ?? 0) > 0 ? '#86efac' : '#fca5a5' }}
                    >
                      {r.listener_change != null ? (r.listener_change > 0 ? '+' : '') + r.listener_change : '—'}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-white/65">
                      {r.classifier_confidence != null
                        ? (r.classifier_confidence * 100).toFixed(0) + '%'
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Overview list ────────────────────────────────────────────────── */
const BreakIntelligence = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API}/api/admin/break-intelligence/overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.status === 401) { navigate('/login'); return; }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setOverview(await r.json());
      } catch (e) {
        setError(String(e.message || e));
      }
    })();
  }, [navigate]);

  if (selected) {
    return (
      <div className="min-h-screen bg-slate-950 text-white p-6">
        <div className="max-w-6xl mx-auto">
          <StationDetail
            stationId={selected}
            onClose={() => setSelected(null)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6">
      <div className="max-w-6xl mx-auto space-y-5">
        <button
          onClick={() => navigate('/admin')}
          className="text-[12px] text-white/55 hover:text-white inline-flex items-center gap-1.5"
          data-testid="break-intel-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Admin Dashboard
        </button>

        <div className="flex items-center gap-3">
          <Brain className="w-8 h-8 text-amber-400" />
          <div>
            <h1 className="text-3xl font-bold">Break Intelligence</h1>
            <p className="text-white/55 text-[13px]">
              Per-station learning profiles. Multi-tenant isolated.
              Observational subsystem — does NOT alter classification.
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-300">
            <AlertCircle className="inline w-4 h-4 mr-2" /> {error}
          </div>
        )}

        {overview && (
          <>
            <div className="bg-slate-900/40 border border-white/10 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-white/55" />
                  <span className="text-sm font-semibold uppercase tracking-wider text-white/80">
                    Stations ({overview.total_stations})
                  </span>
                </div>
                {overview.is_admin && (
                  <span className="text-[10px] uppercase text-amber-400 font-semibold">
                    admin view
                  </span>
                )}
              </div>

              {overview.stations.length === 0 ? (
                <div className="text-center py-12 text-white/45 italic">
                  No learning profiles yet — break_history is empty for all
                  accessible stations. Use the per-station view to trigger a
                  14-day backfill.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]" data-testid="break-intel-overview-table">
                    <thead className="text-white/45 uppercase text-[10px]">
                      <tr>
                        <th className="text-left py-2 pr-3">Station</th>
                        <th className="text-left py-2 pr-3">Status</th>
                        <th className="text-right py-2 pr-3">Total Breaks</th>
                        <th className="text-right py-2 pr-3">Days</th>
                        <th className="text-right py-2 pr-3">Avg Duration</th>
                        <th className="text-right py-2 pr-3">Avg Songs Before</th>
                        <th className="text-right py-2 pr-3">Last Updated</th>
                        <th className="text-right py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.stations.map((s) => {
                        const hex = STATUS_HEX[s.learning_status] || STATUS_HEX.collecting;
                        return (
                          <tr
                            key={s.station_id}
                            className="border-t border-white/5 hover:bg-white/5"
                            data-testid={`break-intel-row-${s.station_id}`}
                          >
                            <td className="py-2 pr-3">{s.station_name || s.station_id}</td>
                            <td className="py-2 pr-3">
                              <span
                                className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase"
                                style={{ backgroundColor: `${hex}22`, color: hex }}
                              >
                                {s.learning_status}
                              </span>
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums">
                              {fmtNum(s.total_breaks)}
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums">
                              {fmtNum(s.days_with_data)}
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums">
                              {fmtSec(s.avg_break_duration_sec)}
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums">
                              {(s.avg_songs_before_break || 0).toFixed(1)}
                            </td>
                            <td className="py-2 pr-3 text-right text-white/55 tabular-nums">
                              {s.last_updated
                                ? new Date(s.last_updated).toLocaleDateString('el-GR')
                                : '—'}
                            </td>
                            <td className="py-2 text-right">
                              <button
                                onClick={() => setSelected(s.station_id)}
                                className="px-2 py-1 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-200 rounded text-[11px]"
                                data-testid={`break-intel-view-${s.station_id}`}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Quick access — admin can drill into ANY station id manually */}
            {overview.is_admin && (
              <div className="bg-slate-900/40 border border-white/10 rounded-xl p-5">
                <div className="text-sm font-semibold uppercase tracking-wider text-white/80 mb-3">
                  Quick lookup
                </div>
                <form
                  className="flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const v = e.target.elements.sid.value.trim();
                    if (v) setSelected(v);
                  }}
                >
                  <input
                    name="sid"
                    placeholder="Station ID (ObjectId)"
                    className="flex-1 bg-white/5 border border-white/15 rounded px-3 py-2 text-[12px] font-mono"
                    data-testid="break-intel-quick-lookup-input"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-200 rounded text-[12px]"
                    data-testid="break-intel-quick-lookup-submit"
                  >
                    Open
                  </button>
                </form>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BreakIntelligence;
